import React, {Component} from "react";
import {connect} from "react-redux";
import {nest as d3Nest} from "d3-collection";
import {max as d3Max, min as d3Min} from "d3-array";
import Source from "./../../../items/Source";
import MiniScaleLegend from "./../../../items/MiniScaleLegend";
import {MATRIX_COLORS} from "./../../../../helpers/colors";
import {toMillions, toPercentage, createMatrixScale, createTextColorScale} from "./../../../../helpers/formatters";

import "./AverageValuesPerClusters.css";

class AverageValuesPerClusters extends Component {

  constructor(props) {
    super();
    this.state = {
      clusterData: false
    }
    //Field list
    this.fieldList = {
      'gdp_per_capita_eur': x => x.toFixed(1),
      'gdp_per_capita_cagr_2007_2018_in_share': x => toPercentage(x, 1),
      'unemployment_rate_share': x => x.toFixed(1),
      'net_migration_rate_2018': x => x.toFixed(1),
      'share_of_employment_in_high_growth_industries_share': x => toPercentage(x, 1),
      'population_with_tertiary_education_2018_in_share': x => x.toFixed(1),
      'old_age_dependency_ratio_2018': x => x.toFixed(1)
    };
    this.prepareData = this.prepareData.bind(this);
  }

  componentDidMount() {
    if(this.props.clusters){
      this.prepareData(this.props.clusters);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.clusters && this.props.clusters) {
      this.prepareData(this.props.clusters);
    }
  }

  prepareData(clusters){
    //Calculate scales
    const scales = {
      back:{},
      color:{}
    };
    Object.keys(this.fieldList).forEach(field => {
      if (this.fieldList[field]){
        const max = d3Max(clusters, r => r[field]);
        const min = d3Min(clusters, r => r[field]);
        scales.back[field] = createMatrixScale(min, max);
        scales.color[field] = createTextColorScale(min, max);
      }
    });

    //Calculate style & formatted values just once
    const formattedData = d3Nest()
      .key(function (d) {return d.cluster_group_name;})
      .entries(clusters.filter(cl => cl.cluster_group_id!==0).map(item=>{
        Object.keys(this.fieldList).forEach(field => {
          item[`${field}_formatted`] = this.fieldList[field]?this.fieldList[field](item[field]):'-';
          item[`${field}_style`] = {
            backgroundColor: this.fieldList[field] ? scales.back[field](item[field]):'#ddd',
            color: this.fieldList[field] ? scales.color[field](item[field]):'grey',
          }
          item[`share_EU_total`] = Math.round(item.number_of_regions_within_cluster * 100 / 1095); //Total of regions
          item[`share_EU_total`] = item[`share_EU_total`] === 0 ? '<1' : item[`share_EU_total`];
        });
        return item;
      }))
      .sort((a, b) => a.values[0].cluster_group_id > b.values[0].cluster_group_id ? 1 : -1);

    //Change state
    this.setState({clusterData: formattedData})
  }

  render() {
    const {clusterData} = this.state;

    if (!clusterData){
      return null;
    }

    return (
      <div id="fow-heatmap-grid">
        <MiniScaleLegend labelLess="Less economically favorable" labelMore="More economically favorable" colors={MATRIX_COLORS} />
        <table className="fow-table fow-font-text">
          <thead>
          <tr>
            <th colSpan="2"></th>
            <th className="fow-th-title-1 fow-font-size-sm">Regions</th>
            <th className="fow-th-title-1 fow-font-size-sm">Population</th>
            <th className="fow-th-title-1 fow-font-size-sm" colSpan="4">Economic indicator</th>
            <th className="fow-th-title-1 fow-font-size-sm">Industry mix</th>
            <th className="fow-th-title-1 fow-font-size-sm" colSpan="2">Labor market</th>
          </tr>
          <tr>
            <th className="fow-font-size-sm" colSpan="2"></th>
            <th className="fow-th-datum fow-font-size-sm"># (Share of EU total, %)</th>
            <th className="fow-th-datum fow-font-size-sm">Millions (share of EU total, %)</th>
            <th className="fow-th-datum fow-font-size-sm">GDP per capita, thousands, EUR</th>
            <th className="fow-th-datum fow-font-size-sm">Real GDP CARG, 2013-18</th>
            <th className="fow-th-datum fow-font-size-sm">Unemployment rate, %</th>
            <th className="fow-th-datum fow-font-size-sm">Net migration rate</th>
            <th className="fow-th-datum fow-font-size-sm">Share of employment in high-growth industries, %</th>
            <th className="fow-th-datum fow-font-size-sm">Share of population with tertiary education, %</th>
            <th className="fow-th-datum fow-font-size-sm">Old-age dependency ratio</th>
          </tr>
          </thead>
          <tbody>
          {
            clusterData.map(group => {
              return group.values.map((cluster, ix) => (
                  <tr key={ix}>
                    {ix===0 &&
                    <td className="fow-td-group fow-td-rotate fow-font-size-sm" rowSpan={group.values.length}><div><span>{cluster.cluster_group_name}</span></div></td>
                    }
                    <td className="fow-td-cluster fow-font-size-sm">{cluster.cluster_name}</td>
                    <td className="fow-td-data fow-font-size-sm">{cluster.number_of_regions_within_cluster} ({cluster.share_EU_total}%)</td>
                    <td className="fow-td-data fow-font-size-sm">{toMillions(cluster.total_population_within_cluster_2018_in_m)} ({toPercentage(cluster.share_of_eu_population_within_cluster_2018_in_share,0)}%)</td>
                    {
                      Object.keys(this.fieldList).map(field =>
                      <td key={field} className="fow-td-datum fow-font-size-sm" style={cluster[`${field}_style`]}>
                        {cluster[`${field}_formatted`]}
                      </td>
                    )}
                  </tr>
                ));
            })
          }
          </tbody>
        </table>
        <Source text="Source: Eurostat, Oxford Economics; McKinsey Global Institute analysis" />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    clusters: state.db.data.dimensions.clusters ? state.db.data.dimensions.clusters.filter(c => c.description_of_cluster !== '') : false,
  };
};
const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AverageValuesPerClusters);
